import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {DilutionAssignmentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Method} from 'procedures/ProcedureDomainTypes';
import FieldError from 'ui-library/FieldError';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';

import FieldLabel from '../../../ui-library/inputs/FieldLabel';
import {
  isValidDilutionAssignment,
  INVALID_DILUTION_ASSIGNMENT_ERROR_MESSAGE,
} from '../validation/private-validation-helpers/validateDilutionPlan';

interface DilutionAssignmentSelectorProps {
  method: Method;
  onChangeMethod: (methodData: Method) => void;
  locked: boolean;
}

interface DilutionAssignmentOptionProps {
  label: string;
  secondaryLabel: string;
}

function DilutionAssignmentOption(props: DilutionAssignmentOptionProps): JSX.Element {
  return (
    <span>
      <b>{`${props.label}: `}</b>
      {props.secondaryLabel}
    </span>
  );
}

export default function DilutionAssignmentSelector(props: DilutionAssignmentSelectorProps): JSX.Element {
  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.header)}>
        <FieldLabel className={css(styles.label)} label="Dilution assignment" />
      </div>
      <div className={css(styles.radioButtonSelector)}>
        <RadioButtonSelector
          isVertical={true}
          disabled={props.locked}
          options={[
            {
              label: (
                <DilutionAssignmentOption
                  label="Single-point"
                  secondaryLabel="Create a single test order per sample, starting at the default dilution factor."
                />
              ),
              value: DilutionAssignmentType.SINGLE_POINT,
            },
            {
              label: (
                <DilutionAssignmentOption
                  label="Multi-point"
                  secondaryLabel="Create multiple test orders per sample, representing all dilution factors. Requires specifying two or more dilution factors above."
                />
              ),
              value: DilutionAssignmentType.MULTI_POINT,
            },
          ]}
          selectedValue={props.method.dilutionPlan.dilutionAssignment}
          onChange={(newDilutionAssignment: DilutionAssignmentType) => {
            props.onChangeMethod({
              ...props.method,
              dilutionPlan: {...props.method.dilutionPlan, dilutionAssignment: newDilutionAssignment},
            });
          }}
        />
      </div>
      {!isValidDilutionAssignment(props.method.dilutionPlan) && (
        <div className={css(styles.error)}>
          <FieldError message={`${INVALID_DILUTION_ASSIGNMENT_ERROR_MESSAGE}.`} />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: 850,
    marginTop: 24,
  },
  // Styling values match simpleDivTable to align with DilutionsTable
  header: {
    backgroundColor: Color.GRAY_50,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    border: 'solid 1px',
    borderBottomStyle: 'none',
    borderColor: Color.GRAY_200,
  },
  label: {
    // FieldLabel component adds bottom margin of 5, so need to subtract that from bottom padding
    padding: '12px 14px 7px 14px',
  },
  radioButtonSelector: {
    padding: '10px 14px',
    border: 'solid 1px',
    borderColor: Color.GRAY_200,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    fontSize: 13,
  },
  error: {
    paddingLeft: 10,
    fontSize: 13,
  },
});
